import _ from 'lodash'
import {createContext, FC, PropsWithChildren, useContext, useReducer} from 'react'

import {SuccessModal, SuccessModalContextProps} from '../SuccessModal'

type SuccessModalContextType = {
  showSuccessModal: (props: SuccessModalContextProps) => void
  closeSuccessModal: () => void
}

const SuccessModalContext = createContext<SuccessModalContextType>({
  showSuccessModal: _.identity,
  closeSuccessModal: _.identity,
})

export const useSuccessModalContext = (): SuccessModalContextType => useContext(SuccessModalContext)

type State = SuccessModalContextProps & {
  isVisible: boolean
}

enum Actions {
  HIDE = 'hide',
  SHOW = 'show',
}

const initialState = {
  title: '',
  description: '',
  image: undefined,
  hasEmailClients: false,
  hasSpamDescription: false,
  buttons: [],
  maxContentWidth: '',
  isVisible: false,
} as State

const reducer = (state: State, action: {type: Actions; payload?: Partial<State>}): State => {
  switch (action.type) {
    case Actions.SHOW:
      return {
        ...state,
        title: action.payload?.title ?? state.title,
        description: action.payload?.description ?? state.description,
        image: action.payload?.image ?? state.image,
        hasEmailClients: action.payload?.hasEmailClients ?? state.hasEmailClients,
        hasSpamDescription: action.payload?.hasSpamDescription ?? state.hasSpamDescription,
        buttons: action.payload?.buttons ?? state.buttons,
        maxContentWidth: action.payload?.maxContentWidth ?? state.maxContentWidth,
        isVisible: true,
      }
    case Actions.HIDE:
      return {...state, isVisible: false}
    default:
      return state
  }
}

export const SuccessModalProvider: FC<PropsWithChildren> = ({children}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const closeSuccessModal = () => dispatch({type: Actions.HIDE})

  const showSuccessModal = (props: SuccessModalContextProps) => {
    dispatch({
      type: Actions.SHOW,
      payload: {...props},
    })
  }

  return (
    <SuccessModalContext.Provider
      value={{
        showSuccessModal,
        closeSuccessModal,
      }}
    >
      {children}
      <SuccessModal
        isVisible={state.isVisible}
        title={state.title}
        description={state.description}
        image={state.image}
        hasEmailClients={state.hasEmailClients}
        hasSpamDescription={state.hasSpamDescription}
        buttons={state.buttons}
        maxContentWidth={state.maxContentWidth}
        onClose={closeSuccessModal}
      />
    </SuccessModalContext.Provider>
  )
}
